// src/services/financeiroService.js
import { pb } from "./pocketbase";
import { logAction } from "./logService";

export const financeiroService = {
  // ─── CLASSIFICAÇÕES ──────────────────────────────────────────────────
  async getClassificacoes() {
    return await pb.collection("fin_classificacoes").getFullList({ sort: "nome" });
  },

  async createClassificacao(name, currentUser) {
    const res = await pb.collection("fin_classificacoes").create({ nome: name });
    await logAction(currentUser, "FINANCEIRO", "CRIAR_CLASSIFICACAO", `Criou classificação "${name}"`, { id: res.id });
    return res;
  },

  async deleteClassificacao(id, name, currentUser) {
    const res = await pb.collection("fin_classificacoes").delete(id);
    await logAction(currentUser, "FINANCEIRO", "DELETAR_CLASSIFICACAO", `Deletou classificação "${name}"`, { id });
    return res;
  },

  // ─── CATEGORIAS ──────────────────────────────────────────────────────
  async getCategorias() {
    return await pb.collection("fin_categorias").getFullList({
      sort: "nome",
      expand: "classificacao",
    });
  },

  async createCategoria(data, currentUser) {
    const res = await pb.collection("fin_categorias").create(data);
    await logAction(currentUser, "FINANCEIRO", "CRIAR_CATEGORIA", `Criou categoria "${data.nome}"`, { id: res.id });
    return res;
  },

  async updateCategoria(id, data, currentUser) {
    const res = await pb.collection("fin_categorias").update(id, data);
    await logAction(currentUser, "FINANCEIRO", "ATUALIZAR_CATEGORIA", `Atualizou categoria "${data.nome}"`, { id });
    return res;
  },

  // ─── CENTROS DE CUSTO ────────────────────────────────────────────────
  async getCentrosCusto() {
    return await pb.collection("fin_centros_custo").getFullList({ sort: "nome" });
  },

  async createCentroCusto(data, currentUser) {
    const res = await pb.collection("fin_centros_custo").create(data);
    await logAction(currentUser, "FINANCEIRO", "CRIAR_CENTRO_CUSTO", `Criou centro de custo "${data.nome}"`, { id: res.id });
    return res;
  },

  async updateCentroCusto(id, data, currentUser) {
    const res = await pb.collection("fin_centros_custo").update(id, data);
    await logAction(currentUser, "FINANCEIRO", "ATUALIZAR_CENTRO_CUSTO", `Atualizou centro de custo "${data.nome}"`, { id });
    return res;
  },

  // ─── CONTAS BANCÁRIAS ────────────────────────────────────────────────
  async getContasBancarias() {
    return await pb.collection("fin_contas_bancarias").getFullList({ sort: "nome" });
  },

  async createContaBancaria(data, currentUser) {
    const res = await pb.collection("fin_contas_bancarias").create(data);
    await logAction(currentUser, "FINANCEIRO", "CRIAR_CONTA_BANCARIA", `Criou conta bancária "${data.nome}"`, { id: res.id });
    return res;
  },

  async updateContaBancaria(id, data, currentUser) {
    const res = await pb.collection("fin_contas_bancarias").update(id, data);
    await logAction(currentUser, "FINANCEIRO", "ATUALIZAR_CONTA_BANCARIA", `Atualizou conta bancária "${data.nome}"`, { id });
    return res;
  },

  // ─── FINANCIAMENTOS ──────────────────────────────────────────────────
  async getFinanciamentos() {
    return await pb.collection("fin_financiamentos").getFullList({
      sort: "-created",
      expand: "centro_custo",
    });
  },

  async createFinanciamento(data, currentUser) {
    const res = await pb.collection("fin_financiamentos").create(data);
    await logAction(currentUser, "FINANCEIRO", "CRIAR_FINANCIAMENTO", `Criou financiamento "${data.nome}"`, { id: res.id });
    return res;
  },

  async updateFinanciamento(id, data, currentUser) {
    const res = await pb.collection("fin_financiamentos").update(id, data);
    await logAction(currentUser, "FINANCEIRO", "ATUALIZAR_FINANCIAMENTO", `Atualizou financiamento "${data.nome}"`, { id });
    return res;
  },

  async deleteFinanciamento(id, name, currentUser) {
    const res = await pb.collection("fin_financiamentos").delete(id);
    await logAction(currentUser, "FINANCEIRO", "DELETAR_FINANCIAMENTO", `Deletou financiamento "${name}"`, { id });
    return res;
  },

  // ─── LEDGER: LANÇAMENTOS ─────────────────────────────────────────────
  async getLancamentosPaginated({ page = 1, perPage = 50, filters = {} }) {
    const filterParts = [];

    if (filters.startDate) {
      filterParts.push(`data >= "${filters.startDate} 00:00:00"`);
    }
    if (filters.endDate) {
      filterParts.push(`data <= "${filters.endDate} 23:59:59"`);
    }
    if (filters.categoria) {
      filterParts.push(`categoria = "${filters.categoria}"`);
    }
    if (filters.centro_custo) {
      filterParts.push(`centro_custo = "${filters.centro_custo}"`);
    }
    if (filters.conta_bancaria) {
      filterParts.push(`conta_bancaria = "${filters.conta_bancaria}"`);
    }
    if (filters.status) {
      filterParts.push(`status = "${filters.status}"`);
    }
    if (filters.tipo_lancamento) {
      filterParts.push(`tipo_lancamento = "${filters.tipo_lancamento}"`);
    }
    if (filters.search) {
      const escaped = filters.search.replace(/"/g, '\\"');
      filterParts.push(`descricao ~ "${escaped}"`);
    }

    const filterString = filterParts.join(" && ");

    return await pb.collection("fin_lancamentos").getList(page, perPage, {
      sort: "-data,-created",
      filter: filterString || null,
      expand: "categoria,centro_custo,conta_bancaria,categoria.classificacao",
    });
  },

  async createLancamentoManual(data, currentUser, bloqueioRigido = false) {
    await this.checkPeriodoFechado(data.data, bloqueioRigido);

    // Invariante I1, I8 e Injeção de Rastreabilidade I7
    const payload = {
      ...data,
      origem: "manual",
      tipo_lancamento: "normal",
    };

    const res = await pb.collection("fin_lancamentos").create(payload);
    await logAction(currentUser, "FINANCEIRO", "LANÇAMENTO_MANUAL_CRIADO", `Criou lançamento manual: ${data.descricao}`, {
      id: res.id,
      valor_centavos: data.valor_centavos,
    });

    await this.registerSummaryForCreate(res);
    if (res.status === "pago") {
      await this.triggerFinanciamentoUpdate(res, currentUser);
    }
    return res;
  },

  async updateLancamento(id, updates, currentUser, bloqueioRigido = false) {
    const original = await pb.collection("fin_lancamentos").getOne(id);

    // Identifica se possui alteração de campos essenciais (valor, descrição, data caixa)
    const hasCoreKeys = ["valor_centavos", "descricao", "data"].some(k => k in updates);
    const isHistory = !!(original.transacao_bancaria || await this.isPeriodoFechado(original.data));

    if (hasCoreKeys) {
      if (isHistory) {
        throw new Error("Não é permitido alterar valor, descrição ou data de lançamentos conciliados ou de meses fechados diretamente. Utilize estorno.");
      }
      // Checa fechamento apenas para edições de campos essenciais em rascunhos
      await this.checkPeriodoFechado(original.data, bloqueioRigido);
      if (updates.data) {
        await this.checkPeriodoFechado(updates.data, bloqueioRigido);
      }
    }

    // Invariante I1 estendida para reclassificações gerenciais e edição direta de rascunhos
    const allowedKeys = [
      "observacoes", 
      "valor_conciliado_centavos", 
      "transacao_bancaria",
      "status",
      "conta_bancaria",
      "data",
      "forma_pagamento",
      "categoria",
      "centro_custo",
      "tipo_compromisso",
      "data_competencia",
      "grupo_semana",
      "valor_centavos",
      "descricao"
    ];
    const filteredUpdates = {};
    let keysUpdated = [];

    for (const key of Object.keys(updates)) {
      if (allowedKeys.includes(key)) {
        filteredUpdates[key] = updates[key];
        keysUpdated.push(key);
      }
    }

    if (keysUpdated.length === 0) {
      throw new Error("Operação inválida. Modificações no Ledger restritas pela invariante I1.");
    }

    const res = await pb.collection("fin_lancamentos").update(id, filteredUpdates);
    await logAction(
      currentUser,
      "FINANCEIRO",
      "LANÇAMENTO_ATUALIZADO",
      `Atualizou campos (${keysUpdated.join(", ")}) do lançamento ID: ${id}`,
      { id, updates: filteredUpdates }
    );

    // Atualiza resumo incremental
    await this.registerSummaryForUpdate(original, filteredUpdates);

    // Se mudou para pago, atualiza financiamentos associados
    if (original.status !== "pago" && res.status === "pago") {
      await this.triggerFinanciamentoUpdate(res, currentUser);
    }

    return res;
  },

  async deleteLancamento(id, currentUser, bloqueioRigido = false) {
    const record = await pb.collection("fin_lancamentos").getOne(id);
    await this.checkPeriodoFechado(record.data, bloqueioRigido);

    const res = await pb.collection("fin_lancamentos").delete(id);
    await logAction(currentUser, "FINANCEIRO", "LANÇAMENTO_DELETADO", `Deletou lançamento ID: ${id}`, { id });
    
    await this.registerSummaryForDelete(record);
    return res;
  },

  async estornarLancamento(originalId, motivo, currentUser, bloqueioRigido = false) {
    // Invariante I2: Correção por estorno cronológico
    const original = await pb.collection("fin_lancamentos").getOne(originalId);
    await this.checkPeriodoFechado(original.data, bloqueioRigido);
    
    const hojeStr = new Date().toISOString().split("T")[0];
    await this.checkPeriodoFechado(hojeStr, bloqueioRigido);
    
    if (original.tipo_lancamento === "estorno") {
      throw new Error("Não é possível estornar um lançamento que já é de estorno.");
    }

    // Verifica se já existe estorno para este ID
    const estornos = await pb.collection("fin_lancamentos").getFullList({
      filter: `referencia_estorno_de = "${originalId}" && tipo_lancamento = "estorno"`,
    });
    if (estornos.length > 0) {
      throw new Error("Este lançamento já foi estornado anteriormente.");
    }

    // Cria o lançamento inverso
    const estornoPayload = {
      data: hojeStr,
      centro_custo: original.centro_custo,
      categoria: original.categoria,
      descricao: `ESTORNO: ${original.descricao} (Motivo: ${motivo})`,
      valor_centavos: -original.valor_centavos, // Inverte o valor
      status: original.status, // Espelha status
      tipo_lancamento: "estorno",
      referencia_estorno_de: originalId,
      conta_bancaria: original.conta_bancaria,
      forma_pagamento: original.forma_pagamento,
      grupo_semana: original.grupo_semana,
      origem: original.origem,
      origem_id: original.origem_id,
      observacoes: `Estorno referente ao lançamento original ID: ${originalId}`,
    };

    const res = await pb.collection("fin_lancamentos").create(estornoPayload);
    await logAction(currentUser, "FINANCEIRO", "LANÇAMENTO_ESTORNADO", `Estornou lançamento ID: ${originalId}. Novo ID: ${res.id}`, {
      originalId,
      estornoId: res.id,
      motivo,
    });

    await this.registerSummaryForCreate(res);
    return res;
  },

  async getEstornosForIds(ids) {
    if (!ids || ids.length === 0) return [];
    const filter = ids.map(id => `referencia_estorno_de = "${id}"`).join(" || ");
    return await pb.collection("fin_lancamentos").getFullList({
      filter: `(${filter}) && tipo_lancamento = "estorno"`,
    });
  },

  async createTransferencia({ contaOrigemId, contaDestinoId, valorCentavos, data, descricao }, currentUser, bloqueioRigido = false) {
    await this.checkPeriodoFechado(data, bloqueioRigido);
    const transferenciaGrupoId = "TX-" + Math.random().toString(36).substring(2, 9).toUpperCase();

    const categorias = await pb.collection("fin_categorias").getFullList();
    const centros = await pb.collection("fin_centros_custo").getFullList({ filter: "ativo = true" });

    const centroPadrao = centros[0]?.id || "";
    const catDespesaMatch = categorias.find(c => c.tipo === "despesa" && (c.nome.toUpperCase().includes("TRANSF") || c.nome.toUpperCase().includes("REPASSE"))) || categorias.find(c => c.tipo === "despesa");
    const catReceitaMatch = categorias.find(c => c.tipo === "receita" && (c.nome.toUpperCase().includes("TRANSF") || c.nome.toUpperCase().includes("REPASSE"))) || categorias.find(c => c.tipo === "receita");

    if (!catDespesaMatch || !catReceitaMatch || !centroPadrao) {
      throw new Error("Configure ao menos uma Categoria (Receita/Despesa) e um Centro de Custo ativos antes de transferir.");
    }

    // 1. Lança a saída na conta de origem
    const saida = await pb.collection("fin_lancamentos").create({
      data,
      descricao: `[Transf. Saída] ${descricao}`,
      valor_centavos: -Math.abs(valorCentavos), // Saída é despesa (negativo)
      conta_bancaria: contaOrigemId,
      status: "pago",
      origem: "transferencia",
      origem_id: transferenciaGrupoId,
      tipo_lancamento: "normal",
      categoria: catDespesaMatch.id,
      centro_custo: centroPadrao
    });

    // 2. Lança a entrada na conta de destino
    const entrada = await pb.collection("fin_lancamentos").create({
      data,
      descricao: `[Transf. Entrada] ${descricao}`,
      valor_centavos: Math.abs(valorCentavos), // Entrada é receita (positivo)
      conta_bancaria: contaDestinoId,
      status: "pago",
      origem: "transferencia",
      origem_id: transferenciaGrupoId,
      tipo_lancamento: "normal",
      categoria: catReceitaMatch.id,
      centro_custo: centroPadrao
    });

    await logAction(currentUser, "FINANCEIRO", "TRANSFERENCIA_REALIZADA", `Transferiu R$ ${valorCentavos / 100} da conta ${contaOrigemId} para ${contaDestinoId}`, {
      transferenciaGrupoId,
      saidaId: saida.id,
      entradaId: entrada.id,
      valor_centavos: valorCentavos,
    });

    await this.registerSummaryForCreate(saida);
    await this.registerSummaryForCreate(entrada);

    return { saida, entrada, transferenciaGrupoId };
  },

  // ─── FECHAMENTO E RESUMO MENSAL (FASE 4) ──────────────────────────────
  async getFechamentos() {
    try {
      return await pb.collection("fin_fechamentos").getFullList({ sort: "-ano,-mes" });
    } catch (err) {
      console.warn("Erro ao buscar fechamentos:", err.message);
      return [];
    }
  },

  async isPeriodoFechado(dataStr) {
    if (!dataStr) return false;
    try {
      const date = new Date(dataStr + "T12:00:00Z");
      const ano = date.getFullYear();
      const mes = date.getMonth() + 1;

      const fechamentos = await pb.collection("fin_fechamentos").getFullList({
        filter: `ano = ${ano} && mes = ${mes}`,
      });

      const ativo = fechamentos.find(f => !f.reaberto_por);
      return !!ativo;
    } catch (err) {
      console.warn("Erro ao checar fechamento:", err.message);
      return false;
    }
  },

  async checkPeriodoFechado(dataStr, bloqueioRigido = false) {
    const isClosed = await this.isPeriodoFechado(dataStr);
    if (isClosed && bloqueioRigido) {
      throw new Error(`Operação bloqueada: o período correspondente a esta transação está FECHADO.`);
    }
    return isClosed;
  },

  async fecharMes({ ano, mes }, currentUser) {
    const res = await pb.collection("fin_fechamentos").create({
      ano,
      mes,
      fechado_por: currentUser.id,
      data_fechamento: new Date().toISOString(),
    });
    await logAction(currentUser, "FINANCEIRO", "MES_FECHADO", `Fechou o período ${mes}/${ano}`, { ano, mes, id: res.id });
    return res;
  },

  async reabrirMes({ ano, mes, motivo }, currentUser) {
    const list = await pb.collection("fin_fechamentos").getFullList({
      filter: `ano = ${ano} && mes = ${mes}`,
    });
    const ativo = list.find(f => !f.reaberto_por);
    if (!ativo) {
      throw new Error("Não foi encontrado um fechamento ativo para este período.");
    }

    const res = await pb.collection("fin_fechamentos").update(ativo.id, {
      reaberto_por: currentUser.id,
      data_reabertura: new Date().toISOString(),
      motivo_reabertura: motivo,
    });

    await logAction(currentUser, "FINANCEIRO", "MES_REABERTO", `Reabriu o período ${mes}/${ano} pelo motivo: ${motivo}`, { ano, mes, motivo, id: res.id });
    return res;
  },

  async updateResumoMensal(ano, mes, centroCustoId, categoriaId, diffs) {
    if (!ano || !mes || !centroCustoId || !categoriaId) return;
    try {
      let record;
      try {
        record = await pb.collection("fin_resumo_mensal").getFirstListItem(
          `ano = ${ano} && mes = ${mes} && centro_custo = "${centroCustoId}" && categoria = "${categoriaId}"`
        );
      } catch (err) {
        if (err.status === 404) {
          record = await pb.collection("fin_resumo_mensal").create({
            ano,
            mes,
            centro_custo: centroCustoId,
            categoria: categoriaId,
            total_pago_centavos: 0,
            total_previsto_centavos: 0,
            total_estornado_centavos: 0,
            qtd_lancamentos: 0,
          });
        } else {
          throw err;
        }
      }

      const payload = {
        total_pago_centavos: Math.max(0, (record.total_pago_centavos || 0) + (diffs.diffPago || 0)),
        total_previsto_centavos: Math.max(0, (record.total_previsto_centavos || 0) + (diffs.diffPrevisto || 0)),
        total_estornado_centavos: Math.max(0, (record.total_estornado_centavos || 0) + (diffs.diffEstornado || 0)),
        qtd_lancamentos: Math.max(0, (record.qtd_lancamentos || 0) + (diffs.diffQtd || 0)),
      };

      await pb.collection("fin_resumo_mensal").update(record.id, payload);
    } catch (err) {
      console.warn("Erro ao atualizar resumo mensal:", err.message);
    }
  },

  async recalcularResumoMensal(ano, mes) {
    try {
      const pad = (n) => String(n).padStart(2, "0");
      const startDate = `${ano}-${pad(mes)}-01 00:00:00`;
      const lastDay = new Date(ano, mes, 0).getDate();
      const endDate = `${ano}-${pad(mes)}-${pad(lastDay)} 23:59:59`;

      const lancamentos = await pb.collection("fin_lancamentos").getFullList({
        filter: `data >= "${startDate}" && data <= "${endDate}"`,
      });

      const aggregates = {};
      lancamentos.forEach((l) => {
        if (!l.centro_custo || !l.categoria) return;
        const key = `${l.centro_custo}_${l.categoria}`;
        if (!aggregates[key]) {
          aggregates[key] = {
            total_pago: 0,
            total_previsto: 0,
            total_estornado: 0,
            qtd: 0,
          };
        }
        
        aggregates[key].qtd += 1;
        if (l.status === "pago") {
          aggregates[key].total_pago += (l.valor_centavos || 0);
        } else if (l.status === "a_vencer" || l.status === "provisao") {
          aggregates[key].total_previsto += (l.valor_centavos || 0);
        }
        if (l.tipo_lancamento === "estorno") {
          aggregates[key].total_estornado += (l.valor_centavos || 0);
        }
      });

      const existingResumos = await pb.collection("fin_resumo_mensal").getFullList({
        filter: `ano = ${ano} && mes = ${mes}`,
      });

      for (const res of existingResumos) {
        const key = `${res.centro_custo}_${res.categoria}`;
        if (!aggregates[key]) {
          await pb.collection("fin_resumo_mensal").delete(res.id);
        }
      }

      for (const [key, val] of Object.entries(aggregates)) {
        const [centroCusto, categoria] = key.split("_");
        const existing = existingResumos.find(r => r.centro_custo === centroCusto && r.categoria === categoria);
        
        const payload = {
          ano,
          mes,
          centro_custo: centroCusto,
          categoria: categoria,
          total_pago_centavos: val.total_pago,
          total_previsto_centavos: val.total_previsto,
          total_estornado_centavos: val.total_estornado,
          qtd_lancamentos: val.qtd,
        };

        if (existing) {
          await pb.collection("fin_resumo_mensal").update(existing.id, payload);
        } else {
          await pb.collection("fin_resumo_mensal").create(payload);
        }
      }
    } catch (err) {
      console.error(`Erro ao recalcular resumo do período ${mes}/${ano}:`, err.message);
      throw err;
    }
  },

  async registerSummaryForCreate(record) {
    const dateObj = new Date(record.data + "T12:00:00Z");
    const ano = dateObj.getFullYear();
    const mes = dateObj.getMonth() + 1;
    const diffs = {
      diffPago: record.status === "pago" ? record.valor_centavos : 0,
      diffPrevisto: (record.status === "a_vencer" || record.status === "provisao") ? record.valor_centavos : 0,
      diffEstornado: record.tipo_lancamento === "estorno" ? record.valor_centavos : 0,
      diffQtd: 1
    };
    await this.updateResumoMensal(ano, mes, record.centro_custo, record.categoria, diffs);
  },

  async registerSummaryForDelete(record) {
    const dateObj = new Date(record.data + "T12:00:00Z");
    const ano = dateObj.getFullYear();
    const mes = dateObj.getMonth() + 1;
    const diffs = {
      diffPago: record.status === "pago" ? -record.valor_centavos : 0,
      diffPrevisto: (record.status === "a_vencer" || record.status === "provisao") ? -record.valor_centavos : 0,
      diffEstornado: record.tipo_lancamento === "estorno" ? -record.valor_centavos : 0,
      diffQtd: -1
    };
    await this.updateResumoMensal(ano, mes, record.centro_custo, record.categoria, diffs);
  },

  async registerSummaryForUpdate(original, updates) {
    const origDate = new Date(original.data + "T12:00:00Z");
    const origAno = origDate.getFullYear();
    const origMes = origDate.getMonth() + 1;

    const newDateStr = updates.data || original.data;
    const newDate = new Date(newDateStr + "T12:00:00Z");
    const newAno = newDate.getFullYear();
    const newMes = newDate.getMonth() + 1;

    const origStatus = original.status;
    const newStatus = updates.status || original.status;

    const oldPago = origStatus === "pago" ? original.valor_centavos : 0;
    const oldPrevisto = (origStatus === "a_vencer" || origStatus === "provisao") ? original.valor_centavos : 0;
    const oldEstornado = original.tipo_lancamento === "estorno" ? original.valor_centavos : 0;

    const newPago = newStatus === "pago" ? original.valor_centavos : 0;
    const newPrevisto = (newStatus === "a_vencer" || newStatus === "provisao") ? original.valor_centavos : 0;
    const newEstornado = original.tipo_lancamento === "estorno" ? original.valor_centavos : 0;

    if (origAno === newAno && origMes === newMes) {
      const diffs = {
        diffPago: newPago - oldPago,
        diffPrevisto: newPrevisto - oldPrevisto,
        diffEstornado: newEstornado - oldEstornado,
        diffQtd: 0
      };
      await this.updateResumoMensal(origAno, origMes, original.centro_custo, original.categoria, diffs);
    } else {
      const oldDiffs = {
        diffPago: -oldPago,
        diffPrevisto: -oldPrevisto,
        diffEstornado: -oldEstornado,
        diffQtd: -1
      };
      const newDiffs = {
        diffPago: newPago,
        diffPrevisto: newPrevisto,
        diffEstornado: newEstornado,
        diffQtd: 1
      };
      await this.updateResumoMensal(origAno, origMes, original.centro_custo, original.categoria, oldDiffs);
      await this.updateResumoMensal(newAno, newMes, original.centro_custo, original.categoria, newDiffs);
    }
  },

  async triggerFinanciamentoUpdate(lancamento, currentUser) {
    if (!lancamento || lancamento.status !== "pago" || !lancamento.descricao) return;
    try {
      const financs = await pb.collection("fin_financiamentos").getFullList();
      const descUpper = lancamento.descricao.toUpperCase();
      
      const match = financs.find(f => descUpper.includes(f.nome.toUpperCase()));
      if (match) {
        const novoSaldo = Math.max(0, (match.saldo_devedor_centavos || 0) - Math.abs(lancamento.valor_centavos));
        const novaParcela = Math.min(match.parcelas_total, (match.parcela_atual || 0) + 1);
        
        await pb.collection("fin_financiamentos").update(match.id, {
          saldo_devedor_centavos: novoSaldo,
          parcela_atual: novaParcela
        });
        
        await logAction(currentUser, "FINANCEIRO", "FINANCIAMENTO_PARCELA_LIQUIDADA", 
          `Contrato ${match.nome} atualizado pela baixa da parcela no ledger. Novo saldo: R$ ${novoSaldo/100}`, 
          { financiamentoId: match.id, lancamentoId: lancamento.id }
        );
      }
    } catch (err) {
      console.warn("Erro ao atualizar financiamento correspondente:", err.message);
    }
  },

  async getResumosAno(ano) {
    return await pb.collection("fin_resumo_mensal").getFullList({
      filter: `ano = ${ano}`,
      expand: "categoria,centro_custo",
    });
  },

  async getAuditoriaConciliacao(ano) {
    try {
      const lots = await pb.collection("fin_extratos_importados").getFullList({
        sort: "-created",
        expand: "conta_bancaria",
      });
      // Filtra lotes do ano correspondente
      return lots.filter((lot) => {
        if (!lot.periodo_inicio) return false;
        return lot.periodo_inicio.startsWith(String(ano));
      });
    } catch (err) {
      console.warn("Erro ao buscar auditoria de conciliação:", err.message);
      return [];
    }
  },

  async getTiposCompromisso() {
    try {
      return await pb.collection("fin_tipos_compromisso").getFullList({ sort: "nome" });
    } catch (err) {
      console.warn("Erro ao carregar fin_tipos_compromisso:", err.message);
      return [];
    }
  },

  async createTipoCompromisso(name, currentUser) {
    const res = await pb.collection("fin_tipos_compromisso").create({ nome: name, ativo: true });
    await logAction(currentUser, "FINANCEIRO", "CRIAR_TIPO_COMPROMISSO", `Criou tipo de compromisso "${name}"`, { id: res.id });
    return res;
  },

  async updateTipoCompromisso(id, payload, currentUser) {
    const res = await pb.collection("fin_tipos_compromisso").update(id, payload);
    await logAction(currentUser, "FINANCEIRO", "EDITAR_TIPO_COMPROMISSO", `Atualizou tipo de compromisso ID: ${id}`, { id, payload });
    return res;
  },
};
